// @ts-nocheck
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { FieldTimeOutlined } from '@ant-design/icons';
import { DatePicker, Tag, Popover, Button, Tooltip } from 'antd';
import { getDateInISOFormat, getDateWithISOFormat } from '@utils/formatters';
import moment, { Moment } from 'moment';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';

import * as S from './styles';

interface IDateChange {
  startDate?: string;
  endDate?: string;
}

interface IIndividualRangePicker {
  value?: string[]; //  ---> ['YYYYMMdd', 'YYYYMMdd']
  onChange: ({ startDate, endDate }: IDateChange) => void; //  ---> { initialDate: 'YYYYMMdd', finalDate: 'YYYYMMdd' }
  filters?: boolean;
  hideFilters?: boolean;
}

const IndividualRangePicker = ({
  onChange,
  value,
  filters = true,
  hideFilters = false,
}: IIndividualRangePicker) => {
  const { t } = useTranslation('date-picker');
  const { i18n } = useTranslation();
  const { keycloak } = useContext(KeycloakContext);

  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();
  const [showPopOver, setShowPopOver] = useState(false);

  useEffect(() => {
    if (value) {
      setStartDate(getDateWithISOFormat(value[0]));
      setEndDate(getDateWithISOFormat(value[1]));
    }
  }, [value]);

  const whichFormat = useCallback((locale: string) => {
    const localeDictionary = {
      pt: 'DD/MM/YYYY',
      es: 'DD/MM/YYYY',
      en: 'MM/DD/YYYY',
    };
    // @ts-ignore
    return localeDictionary[locale];
  }, []);

  const configDatePicker = useMemo(
    () => ({
      format: whichFormat(
        i18n?.language ?? keycloak?.idTokenParsed?.locale ?? 'pt',
      ),
      size: 'middle',
      style: { width: '49.5%' },
      allowClear: true,
    }),
    [i18n?.language, keycloak?.idTokenParsed?.locale, whichFormat],
  );

  const handleChangeStartDate = useCallback(
    date => {
      const initialDate = date && date.toISOString();
      setStartDate(initialDate);

      onChange({
        startDate: getDateInISOFormat(initialDate),
        endDate: getDateInISOFormat(endDate) ?? undefined,
      });
    },
    [endDate, onChange],
  );

  const handleChangeEndDate = useCallback(
    date => {
      const finalDate = date?.toISOString();
      if (startDate && finalDate && startDate > finalDate) {
        setStartDate(undefined);
        onChange({
          startDate: undefined,
          endDate: getDateInISOFormat(finalDate),
        });
      } else {
        onChange({
          startDate: startDate && getDateInISOFormat(startDate),
          endDate: getDateInISOFormat(finalDate),
        });
      }
      setEndDate(finalDate);
    },
    [onChange, startDate],
  );

  const handlePresetFilters = useCallback(
    momentDay => {
      const dateInISOFormat = momentDay.toISOString();
      setStartDate(undefined);
      setEndDate(dateInISOFormat);
      onChange({
        startDate: undefined,
        endDate: getDateInISOFormat(dateInISOFormat),
      });
    },
    [onChange],
  );

  const tags = useMemo(
    () => [
      {
        name: t('last-week'),
        momentDay: moment().subtract(7, 'days'),
      },
      {
        name: t('last-two-weeks'),
        momentDay: moment().subtract(14, 'days'),
      },
      {
        name: t('last-three-weeks'),
        momentDay: moment().subtract(21, 'days'),
      },
      {
        name: t('last-month'),
        momentDay: moment().subtract(1, 'months'),
      },
      {
        name: t('last-two-months'),
        momentDay: moment().subtract(2, 'months'),
      },
      {
        name: t('last-three-months'),
        momentDay: moment().subtract(3, 'months'),
      },
    ],
    [t],
  );

  return (
    <S.RangePickerContainer>
      <div className="single-range-pickers">
        <DatePicker
          placeholder={t('initial-date')}
          defaultValue={value?.[0] && moment(value[0], 'YYYYMMDD')}
          disabledDate={(d: Moment) => {
            if (hideFilters) {
              return false;
            }
            return d && d > moment().endOf('day');
          }}
          value={startDate && moment(startDate, 'YYYYMMDD')}
          onChange={handleChangeStartDate}
          {...configDatePicker}
        />
        <DatePicker
          placeholder={t('end-date')}
          defaultValue={value?.[1] && moment(value[1], 'YYYYMMDD')}
          value={endDate && moment(endDate, 'YYYYMMDD')}
          onChange={handleChangeEndDate}
          {...configDatePicker}
        />
      </div>
      {filters && !hideFilters && (
        <div className="single-range-popover">
          <Tooltip title={t('preset-ranges')}>
            <Popover
              visible={showPopOver}
              onVisibleChange={setShowPopOver}
              content={
                <div className="tags-wrapper">
                  {tags?.map(({ name, momentDay }) => (
                    <Tag
                      key={name}
                      color="var(--primary-color)"
                      className="tag-quick-filters"
                      onClick={() => handlePresetFilters(momentDay)}
                    >
                      {name}
                    </Tag>
                  ))}
                </div>
              }
              trigger="click"
            >
              <Button
                type="primary"
                icon={<FieldTimeOutlined />}
                style={{ margin: '0 2px' }}
                onClick={() => setShowPopOver(oldShow => !oldShow)}
              />
            </Popover>
          </Tooltip>
        </div>
      )}
    </S.RangePickerContainer>
  );
};

export default memo(IndividualRangePicker);
